import React, { createContext, useEffect, useState } from "react";
import { createContextHook } from "../hooks/useContextFactory";
import {
  SidebarContextType,
  SidebarProviderProps,
  SidebarState,
  SidebarType,
} from "../interfaces/all";
import { CacheManager } from "../utils/cache-manager";

const SIDEBAR_STATE_KEY = "sidebar_state";
const SIDEBAR_STATE_CACHE_OPTIONS = {
  expirationDays: 7, // Estado da sidebar expira em 7 dias
};

const defaultState: SidebarState = {
  type: "conversations",
  isOpen: true,
};

const SidebarContext = createContext<SidebarContextType>({
  sidebarType: defaultState.type,
  setSidebarType: () => {},
  isOpen: defaultState.isOpen,
  setIsOpen: () => {},
});

export const useSidebar = createContextHook(SidebarContext, "useSidebar");

export const SidebarProvider: React.FC<SidebarProviderProps> = ({
  children,
}) => {
  // Inicializa o estado a partir do cache ou usa o estado padrão
  const [sidebarType, setSidebarType] = useState<SidebarType>(() => {
    const cachedState = CacheManager.get<SidebarState>(
      SIDEBAR_STATE_KEY,
      SIDEBAR_STATE_CACHE_OPTIONS,
    );
    return cachedState?.type ?? defaultState.type;
  });

  const [isOpen, setIsOpen] = useState<boolean>(() => {
    const cachedState = CacheManager.get<SidebarState>(
      SIDEBAR_STATE_KEY,
      SIDEBAR_STATE_CACHE_OPTIONS,
    );
    return cachedState?.isOpen ?? defaultState.isOpen;
  });

  // Salva o estado no cache sempre que ele mudar
  useEffect(() => {
    CacheManager.set(
      SIDEBAR_STATE_KEY,
      { type: sidebarType, isOpen },
      SIDEBAR_STATE_CACHE_OPTIONS,
    );
  }, [sidebarType, isOpen]);

  return (
    <SidebarContext.Provider
      value={{ sidebarType, setSidebarType, isOpen, setIsOpen }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
